import {
  type GetWorkspaceRolePermissionsQuery,
  getWorkspaceRolePermissionsQuery,
} from '@affine/graphql';
import type { LiveData } from '@toeverything/infra';
import { Store } from '@toeverything/infra';

import { defaultRequestCoordinator } from '../../cloud/services/request-coordinator';
import { defaultWorkspaceApiClient } from '../../cloud/services/workspace-api-client';
import type { WorkspaceServerService } from '../../cloud/services/workspace-server';
import { getLocalAuthorityClientInstallId } from '../../local-authority';
import type { WorkspaceService } from '../../workspace';
import {
  type PermissionSnapshotResponse,
  PermissionSnapshotStore,
} from './permission-snapshot';

export type WorkspacePermissionActions = keyof Omit<
  GetWorkspaceRolePermissionsQuery['workspaceRolePermissions']['permissions'],
  '__typename'
>;

export type DocPermissionActions =
  | 'Doc_Comments_Create'
  | 'Doc_Comments_Delete'
  | 'Doc_Comments_Read'
  | 'Doc_Comments_Resolve'
  | 'Doc_Copy'
  | 'Doc_Delete'
  | 'Doc_Duplicate'
  | 'Doc_Properties_Read'
  | 'Doc_Properties_Update'
  | 'Doc_Publish'
  | 'Doc_Read'
  | 'Doc_Restore'
  | 'Doc_TransferOwner'
  | 'Doc_Trash'
  | 'Doc_Update'
  | 'Doc_Users_Manage'
  | 'Doc_Users_Read';

export function getNoDocPermissions(): Record<DocPermissionActions, boolean> {
  return {
    Doc_Read: false,
    Doc_Update: false,
    Doc_Delete: false,
    Doc_Copy: false,
    Doc_Duplicate: false,
    Doc_Publish: false,
    Doc_Restore: false,
    Doc_Properties_Read: false,
    Doc_Properties_Update: false,
    Doc_Users_Manage: false,
    Doc_Users_Read: false,
    Doc_Trash: false,
    Doc_TransferOwner: false,
    Doc_Comments_Create: false,
    Doc_Comments_Delete: false,
    Doc_Comments_Read: false,
    Doc_Comments_Resolve: false,
  } as Record<DocPermissionActions, boolean>;
}

const DOC_PERMISSION_ACTIONS = Object.keys(getNoDocPermissions());
const MAX_PERMISSION_SNAPSHOT_RESOURCES = 250;
const MAX_PERMISSION_SNAPSHOT_FRESH_ATTEMPTS = 3;

export class GuardStore extends Store {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly permissionSnapshotStore: PermissionSnapshotStore
  ) {
    super();
  }

  async getWorkspacePermissions(): Promise<
    Record<WorkspacePermissionActions, boolean>
  > {
    if (!this.workspaceServerService.server) {
      throw new Error('No server');
    }
    const data = await this.workspaceServerService.server.gql({
      query: getWorkspaceRolePermissionsQuery,
      variables: {
        id: this.workspaceService.workspace.id,
      },
    });
    return data.workspaceRolePermissions.permissions;
  }

  async getDocPermissions(
    docId: string
  ): Promise<Record<DocPermissionActions, boolean>> {
    await this.permissionSnapshotStore.hydrateLocal();
    const cached = this.getCachedDocPermissions(docId);
    if (cached) {
      return cached;
    }
    const permissions = await this.getDocPermissionsBatch([docId]);
    return permissions[docId] ?? getNoDocPermissions();
  }

  async hydrateLocalPermissionSnapshot(): Promise<void> {
    await this.permissionSnapshotStore.hydrateLocal();
  }

  getCachedDocPermissions(
    docId: string
  ): Record<DocPermissionActions, boolean> | null {
    return this.normalizeDocPermissions(
      this.permissionSnapshotStore.getDocPermissions(docId)
    );
  }

  watchCachedDocPermissions(
    docId: string
  ): LiveData<Record<DocPermissionActions, boolean> | null> {
    return this.permissionSnapshotStore.grants$.map(() =>
      this.getCachedDocPermissions(docId)
    );
  }

  observePermissionGraphVersion(version?: number | null): void {
    if (version != null) {
      this.permissionSnapshotStore.observePermissionGraphVersion(version);
    }
  }

  async getDocPermissionsBatch(
    docIds: string[],
    options: { force?: boolean } = {}
  ): Promise<Record<string, Record<DocPermissionActions, boolean>>> {
    await this.permissionSnapshotStore.hydrateLocal();
    const ids = Array.from(new Set(docIds.filter(Boolean)));
    if (ids.length === 0) {
      return {};
    }
    const server = this.workspaceServerService.server;
    if (!server) {
      return this.cachedPermissionsFor(ids);
    }
    const missingIds = options.force
      ? ids
      : ids.filter(id => !this.getCachedDocPermissions(id));
    if (missingIds.length === 0) {
      return this.cachedPermissionsFor(ids);
    }
    try {
      await defaultRequestCoordinator.executeBatch<
        string,
        Record<string, boolean>
      >({
        workspaceId: this.workspaceService.workspace.id,
        category: 'permissions',
        key: 'permission-snapshot:visible-docs',
        priority: 'visible',
        mode: 'background',
        items: missingIds,
        itemKey: id => id,
        maxBatchSize: MAX_PERMISSION_SNAPSHOT_RESOURCES,
        run: async (batch, signal) => {
          const path = new URL(
            `/api/workspaces/${encodeURIComponent(
              this.workspaceService.workspace.id
            )}/permissions/snapshot`,
            server.baseUrl
          ).href;
          for (
            let attempt = 1;
            attempt <= MAX_PERMISSION_SNAPSHOT_FRESH_ATTEMPTS;
            attempt++
          ) {
            const response =
              await defaultWorkspaceApiClient.requestJsonWithinCoordinator<PermissionSnapshotResponse>(
                {
                  workspaceId: this.workspaceService.workspace.id,
                  path,
                  category: 'permissions',
                  mode: 'background',
                  priority: 'visible',
                  init: {
                    method: 'POST',
                    headers: {
                      'content-type': 'application/json',
                    },
                    body: JSON.stringify({
                      clientInstallId: getLocalAuthorityClientInstallId(),
                      visibilityMode: 'user_effective',
                      resources: batch.map(id => ({ id, type: 'doc' })),
                    }),
                  },
                },
                signal
              );
            await this.permissionSnapshotStore.applyOnlineResponse(response);

            // A permissions-changed event can advance the observed graph
            // version while an older signed response is in flight. The store
            // correctly rejects that response and removes stale grants. Fetch
            // again instead of silently converting the transient race into a
            // denied/readonly UI state. Every requested resource has a signed
            // grant, including explicit denied grants, so presence proves that
            // this response was accepted without weakening fail-closed rules.
            if (batch.every(id => this.getCachedDocPermissions(id) !== null)) {
              return Object.fromEntries(
                batch.map(id => [
                  id,
                  this.getCachedDocPermissions(id) ?? getNoDocPermissions(),
                ])
              );
            }
          }

          throw new Error(
            `Permission snapshot remained stale after ${MAX_PERMISSION_SNAPSHOT_FRESH_ATTEMPTS} attempts`
          );
        },
      });
    } catch {
      // The signed local snapshot remains the only offline authority. Unknown
      // docs fail closed instead of falling back to stale discoverability data.
    }
    return this.cachedPermissionsFor(ids);
  }

  private cachedPermissionsFor(
    docIds: string[]
  ): Record<string, Record<DocPermissionActions, boolean>> {
    const result: Record<string, Record<DocPermissionActions, boolean>> = {};
    for (const docId of docIds) {
      result[docId] =
        this.getCachedDocPermissions(docId) ?? getNoDocPermissions();
    }
    return result;
  }

  private normalizeDocPermissions(
    source: Record<string, boolean> | null
  ): Record<DocPermissionActions, boolean> | null {
    if (!source) {
      return null;
    }
    const normalized = getNoDocPermissions();
    for (const action of DOC_PERMISSION_ACTIONS as DocPermissionActions[]) {
      normalized[action] = source[action] === true;
    }
    return normalized;
  }
}
